'use client';
import React from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';

interface Props {
  data: { month: string; gsv: number; activeCount: number; commissions: number }[];
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 shadow-xl">
      <p className="text-xs font-semibold text-zinc-300 mb-1.5">{label} 2025–26</p>
      {payload.map((p: any) => (
        <div key={`gtt-${p.dataKey}`} className="flex justify-between gap-4 text-xs">
          <span style={{ color: p.color }}>{p.name}</span>
          <span className="font-mono text-zinc-200">
            {p.dataKey === 'gsv' ? `${(p.value / 1000).toFixed(1)}K` : p.value}
          </span>
        </div>
      ))}
    </div>
  );
};

export default function GSVTrendChart({ data }: Props) {
  const avg = data.reduce((s, d) => s + d.gsv, 0) / data.length;

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-zinc-300">Network GSV — 12-Month Trend</h3>
        <div className="flex items-center gap-3 text-[10px] text-zinc-600">
          <span>Avg: <span className="text-zinc-400 font-mono">{(avg / 1000).toFixed(1)}K</span></span>
          <span>May 2025 – Apr 2026</span>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={200}>
        <AreaChart data={data} margin={{ top: 4, right: 4, left: -15, bottom: 0 }}>
          <defs>
            <linearGradient id="gsvGradAna" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#7C3AED" stopOpacity={0.35} />
              <stop offset="95%" stopColor="#7C3AED" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="commGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.25} />
              <stop offset="95%" stopColor="#F59E0B" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
          <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#71717a' }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fontSize: 9, fill: '#71717a' }} axisLine={false} tickLine={false} tickFormatter={v => `${(v / 1000).toFixed(0)}K`} />
          <Tooltip content={<CustomTooltip />} />
          <ReferenceLine y={avg} stroke="#52525b" strokeDasharray="4 4" />
          <Area type="monotone" dataKey="gsv" name="GSV" stroke="#7C3AED" strokeWidth={2} fill="url(#gsvGradAna)" dot={false} />
          <Area type="monotone" dataKey="commissions" name="Commissions" stroke="#F59E0B" strokeWidth={1.5} fill="url(#commGrad)" dot={false} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}